/**
 * Gate Connect Page
 * QR 코드로 접속하는 게이트 연결 페이지
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';

const GateConnectPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('게이트 연결 중...');

  const gateId = searchParams.get('id');
  const token = searchParams.get('token');

  useEffect(() => {
    const connectGate = async () => {
      if (!gateId || !token) {
        setStatus('error');
        setMessage('잘못된 QR 코드입니다.');
        return;
      }

      try {
        // 클라이언트 IP 주소 가져오기 (여러 방법 시도)
        let ipAddress = null;
        try {
          // 방법 1: ipify API 사용 (빠르고 간단)
          const ipResponse = await fetch('https://api.ipify.org?format=json');
          if (ipResponse.ok) {
            const ipData = await ipResponse.json();
            ipAddress = ipData.ip;
          }
        } catch (ipError) {
          console.warn('⚠️ IP 조회 실패, 계속 진행:', ipError);
          // IP 조회 실패해도 연결은 계속 진행
        }

        // 백엔드 API 호출 - 게이트 연결 검증
        const apiUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
          ? `http://localhost:8000/api/v1/gates/${gateId}/connect`
          : `/v1/gates/${gateId}/connect`;


        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            token,
            ip_address: ipAddress
          }),
        });


        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const errorMessage = errorData.detail || '게이트 연결에 실패했습니다';
          console.error('❌ 게이트 연결 실패:', errorData);
          throw new Error(errorMessage);
        }

        const result = await response.json();

        setStatus('success');
        setMessage(result.message || '게이트가 성공적으로 연결되었습니다!');

        // 게이트 설정을 localStorage에 저장
        localStorage.setItem('gate_id', gateId);
        localStorage.setItem('gate_token', token);

        // 게이트 정보도 저장 (optional)
        if (result.gate) {
          localStorage.setItem('gate_info', JSON.stringify(result.gate));
        }

        // 🔧 게이트 운영 페이지에서 사용할 설정 저장 (중요!)
        const gateConfig = {
          gate_id: gateId,
          gate_name: result.gate?.name || '게이트',
          organization: result.gate?.location || '독서실',
          direction: result.gate?.direction || 'both',
          setup_time: new Date().toISOString()
        };
        localStorage.setItem('gate_config', JSON.stringify(gateConfig));

        // 2초 후 게이트 얼굴인식 UI로 이동
        setTimeout(() => {
          navigate('/tablet/gate-operation');
        }, 2000);
      } catch (error: any) {
        console.error('💥 게이트 연결 오류:', error);
        setStatus('error');
        setMessage(error.message || '게이트 연결에 실패했습니다.');
      }
    };

    connectGate();
  }, [gateId, token, navigate]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        {status === 'loading' && (
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">연결 중...</h2>
            <p className="text-gray-600">{message}</p>
          </div>
        )}

        {status === 'success' && (
          <div className="text-center">
            <CheckCircleIcon className="h-16 w-16 text-green-600 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">연결 성공!</h2>
            <p className="text-gray-600 mb-4">{message}</p>
            <p className="text-sm text-gray-500">잠시 후 홈 화면으로 이동합니다...</p>
          </div>
        )}

        {status === 'error' && (
          <div className="text-center">
            <XCircleIcon className="h-16 w-16 text-red-600 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">연결 실패</h2>
            <p className="text-gray-600 mb-6">{message}</p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              홈으로 돌아가기
            </button>
          </div>
        )}

        {gateId && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-xs text-gray-500 text-center">
              게이트 ID: {gateId}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default GateConnectPage;
